import React, { useCallback, useState } from "react";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";

import { useUserContext } from "../../contexts/UserContext";
import {
  useFillTaxOfficeBalanceFromTaxAccountMutation,
  VAT_ANNUAL_DECLARATION_QUERY,
} from "../../../../../api/graphql";
import { IMessageType } from "../../../../../types";
import {
  formatAmountInCents,
  showGraphQlErrorNotification,
  showMessage,
} from "../../../../../utils";
import { FetchLinkButton } from "./styledComponents";

export interface FetchTaxOfficeBalanceButtonProps {
  email: string;
  taxYear: number;
}

const FetchTaxOfficeBalanceButton = ({
  email,
  taxYear,
}: FetchTaxOfficeBalanceButtonProps) => {
  const { t } = useTranslation("VatAnnualDeclaration", {
    keyPrefix: "fetchTaxOfficeBalance",
  });
  const user = useUserContext();
  const [loading, setLoading] = useState(false);
  const [fill] = useFillTaxOfficeBalanceFromTaxAccountMutation({
    refetchQueries: [VAT_ANNUAL_DECLARATION_QUERY],
    // Stay pending until the table shows the new value.
    awaitRefetchQueries: true,
  });

  // Only used for the disabled state: the backend picks the tax number itself.
  const hasTaxNumber = Boolean(user?.taxNumber);

  const handleClick = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fill({ variables: { email, year: taxYear } });
      const data = result.data?.fillTaxOfficeBalanceFromTaxAccount;
      if (data) {
        const queryDatePart = data.queryDate
          ? t("queryDatePart", { queryDate: data.queryDate })
          : "";
        showMessage({
          type: IMessageType.SUCCESS,
          content: t("success", {
            amount: formatAmountInCents(data.amount, true),
            taxNumber: data.taxNumber,
            queryDatePart,
          }),
          duration: 8,
        });
      }
    } catch (error) {
      showGraphQlErrorNotification(t("error"), error);
    } finally {
      setLoading(false);
    }
  }, [email, fill, t, taxYear]);

  return (
    <Tooltip title={hasTaxNumber ? t("hint") : t("noTaxNumber")}>
      {/* The span shows the tooltip on a disabled button and keeps the click from toggling the expandable table row. */}
      <span onClick={(event) => event.stopPropagation()}>
        <FetchLinkButton
          type="link"
          size="small"
          // Muted while the lookup runs, so it cannot be started twice.
          disabled={!hasTaxNumber || loading}
          loading={loading}
          onClick={handleClick}
        >
          {t("label")}
        </FetchLinkButton>
      </span>
    </Tooltip>
  );
};

export default FetchTaxOfficeBalanceButton;
